/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';

import NotFoundPage from '../components/404';
import PageData from '../helpers/pageData';
import Layout from '../components/Layouts/Layout';
import Body from '../components/ui/Body/Body';
import getPageContextFromData from '../lib/dto/getPageContextFromData';
import StructuredData from '../helpers/StructuredData';

const ServicePost = ({
  data: { prismicServicePost: { type, uid, data } },
}) => {
  if (!data) return <NotFoundPage />;

  const dataContext = getPageContextFromData(type, uid, data);

  return (
    <PageData.Provider value={dataContext}>
      <StructuredData
        type="Article"
        title={data.seo_title || data.title}
        description={data.seo_description || data.page_short_description}
        pathname={dataContext.url}
        image={data.landscape_image || data.thumbnail_image}
        author="Portable"
      />
      <Layout>
        <Body />
      </Layout>
    </PageData.Provider>
  );
};

export const query = graphql`
query ServicePostBySlug($uid: StringQueryOperatorInput = {}) {
  prismicServicePost(uid: $uid) {
    _previewable
    uid
    type
    data {
      introduction
      page_colour
      page_short_description
      prismic_page_name
      seo_description
      seo_title
      title
      tag
      thumbnail_image {
        localFile {
          publicURL
          extension
          childImageSharp {
            gatsbyImageData(
              layout: CONSTRAINED,
              quality: 100,
              width: 640,
              breakpoints: [640, 960, 1400]
            )
          }
        }
      }
      landscape_image {
        localFile {
          publicURL
          extension
          childImageSharp {
            gatsbyImageData(
              layout: CONSTRAINED,
              quality: 100,
              width: 1400,
              breakpoints: [640, 960, 1400]
            )
          }
        }
      }
      body {
        ... on PrismicServicePostDataBodyContent {
          id
          slice_type
          primary {
            content_heading {
              text
              richText
            }
            content_subtitle {
              richText
              text
            }
            content_text {
              richText
              text
            }
            content_button_text {
              text
            }
            content_button_url {
              raw
            }
            layout
          }
        }
        ... on PrismicServicePostDataBodyImage {
          id
          slice_type
          primary {
            alignment
            image {
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED,
                    quality: 100,
                    width: 1400,
                    breakpoints: [640, 960, 1400]
                  )
                }
              }
            }
          }
        }
        ... on PrismicServicePostDataBodyVideo {
          id
          slice_type
          primary {
            alignment
            video_embeded {
              embed_url
            }
          }
        }
        ... on PrismicServicePostDataBodyQuote {
          id
          slice_type
          primary {
            quote {
              richText
              text
            }
          }
        }
        ... on PrismicServicePostDataBodyBanner {
          id
          slice_type
          primary {
            background_colour
            text_colour
            big_text {
              richText
              text
            }
            text_link {
              richText
              text
            }
          }
        }
        ... on PrismicServicePostDataBodyRelatedContent {
          id
          slice_type
          primary {
            items_to_show
            related_content_title {
              text
            }
          }
        }
        ... on PrismicServicePostDataBodyList {
          id
          slice_type
          primary {
            list_heading {
              text
            }
          }
          items {
            list_items {
              document {
                ... on PrismicListItem {
                  id
                  data {
                    heading {
                      text
                    }
                    description {
                      text
                      richText
                    }
                    icon {
                      alt
                      url
                    }
                  }
                }
              }
            }
          }
        }
        ... on PrismicServicePostDataBodyDownloadFormHs {
          id
          slice_type
          primary {
            form_title
            hubspot_form_id
            form_image {
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED,
                    quality: 100,
                    width: 1400,
                    breakpoints: [640, 960, 1400]
                  )
                }
              }
            }
          }
        }
        ... on PrismicServicePostDataBodyActionButton {
          id
          slice_type
          primary {
            layout
            button_text {
              text
            }
            button_url {
              id
              link_type
              isBroken
              type
              uid
              url
              target
            }
          }
        }
        ... on PrismicServicePostDataBodyAlternatingMediaText {
          id
          slice_type
          items {
            list_description {
              text
            }
            list_heading {
              text
            }
          }
          primary {
            counter
            tag1 {
              text
            }
            content {
              text
            }
            heading {
              text
            }
            image {
              url
              alt
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED,
                    quality: 100,
                    width: 1400,
                    breakpoints: [640, 960, 1400]
                  )
                }
              }
            }
          }
        }
        ... on PrismicServicePostDataBodyOurWork {
          id
          slice_type
          primary {
            link {
              url
              uid
              type
              raw
              id
            }
            link_text {
              text
            }
            title1 {
              text
              richText
            }
          }
          items {
            heading {
              text
            }
            image {
              alt
              url
            }
            taglabel {
              text
            }
            image_link {
              url
              uid
              link_type
              id
            }
          }
        }
        ... on PrismicServicePostDataBodyServiceSuggestion {
          id
          slice_type
          primary {
            title1 {
              text
            }
          }
          items {
            service_link {
              document {
                ... on PrismicServicePost {
                  type
                  uid
                }
              }
            }
            service_title {
              text
            }
          }
        }
        ... on PrismicServicePostDataBodyServicePostHero {
          id
          slice_type
          primary {
            content {
              text
            }
            heading {
              text
            }
            tag {
              text
            }
          }
        }
      }
      process_tags {
        processes {
          slug
          type
        }
      }
      purpose_tags {
        purposes {
          type
          slug
        }
      }
      service_tags {
        services {
          type
          slug
        }
      }
    }
  }
}
`;

ServicePost.propTypes = {
  data: PropTypes.shape({
    prismicServicePost: PropTypes.object.isRequired,
  }).isRequired,
};

export default withPrismicPreview(ServicePost);
